import { apiClient } from './client';
import {
  getSsoClientPortalUrl,
  getSsoCoachDashboardUrl,
  getSsoHealthGuideUrl,
  getSsoZendeskUrl,
} from '../config/runtimeConfig';
import { isZendeskUrl } from '../utils/redirect';
import { Organization, SsoExchangeResponse } from '../types';

const B2B_PARTNERS_URI = '/b2b/api/v1/sso-partners';

// In-memory cache for organizations
let organizationsCache: Organization[] | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes


/**
 * Fetch list of organizations for SSO login (with caching)
 */
export const fetchOrganizations = async (): Promise<Organization[]> => {
  // Check if cache is valid
  const now = Date.now();
  if (organizationsCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION_MS) {
    console.log('Returning cached organizations list');
    return organizationsCache;
  }

  console.log('Fetching organizations from API...');

  const response = await apiClient.get<Organization[]>(B2B_PARTNERS_URI);
  const organizations = response.data;

  // Update cache
  organizationsCache = organizations;
  cacheTimestamp = now;

  return organizations;
};

/**
 * Generate SAML URL with RelayState parameter
 */
export const generateSamlUrl = (ssoUrl: string, relayState?: string): string => {
  if (!relayState) {
    return ssoUrl;
  }

  const url = new URL(ssoUrl);
  url.searchParams.set('RelayState', relayState);
  return url.toString();
};

/**
 * Okta Zendesk app for Vida employees returning to Zendesk.
 *
 * Unlike getSpecialSSOUrl this never auto-redirects — members and employees
 * both arrive with a Zendesk ?return_to, so the caller renders this as a
 * choice. Returns null when ?return_to isn't Zendesk or the app is unset.
 */
export const getZendeskSSOUrl = (returnTo?: string): string | null => {
  if (!isZendeskUrl(returnTo)) return null;
  // Empty means "no Okta app in this env" — hide rather than render a dead button.
  return getSsoZendeskUrl() || null;
};

/**
 * Determine special SSO routing based on next URL
 * Returns the appropriate SSO URL for coach dashboard, health guide platform
 * or Client Command Center
 */
export const getSpecialSSOUrl = (nextUrl?: string): string | null => {
  if (!nextUrl) return null;
  if (nextUrl.startsWith('/coaching') || nextUrl.startsWith('/auditing')) {
    return getSsoCoachDashboardUrl() ?? null;
  }
  if (nextUrl.startsWith('/web/admin')) {
    return getSsoHealthGuideUrl() ?? null;
  }
  if (nextUrl.startsWith('/client-portal')) {
    // Empty means "no Okta app in this env" — keep the org picker rather than
    // redirect to a blank URL.
    return getSsoClientPortalUrl() || null;
  }
  return null;
};

/**
 * Redeem the one-shot SSO code for tokens.
 *
 * The SSO service redirects the browser to /login/sso-callback?code=<opaque>
 * after a successful SAML POST. The code is a one-shot handle that maps to a
 * token bundle stashed in Apigee's cache (60s TTL). The cache entry is
 * invalidated as part of the lookup, so each code can only be redeemed once.
 *
 * Returns the full token bundle + destination metadata for the SPA to navigate.
 */
export const ssoExchange = async (code: string): Promise<SsoExchangeResponse> => {
  const response = await apiClient.post<SsoExchangeResponse>(
    '/v1/auth/sso-exchange',
    { code }
  );
  return response.data;
};
